import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, Outlet, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/toaster.jsx";
import { useToast } from "./hooks/use-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select.jsx";
import { Card } from "./components/ui/card.jsx";
import { Button } from "./components/ui/button.jsx";
import { Input } from "./components/ui/input.jsx";
import StateHub from "./pages/StateHub.jsx";
import TopicInfo from "./pages/TopicInfo.jsx";
import SpeciesLibrary from "./pages/SpeciesLibrary.jsx";
import GearPrep from "./pages/GearPrep.jsx";
import WildlifeAreas from "./pages/WildlifeAreas.jsx";
import { Scale, CalendarDays, MapPinned, PawPrint, Backpack, Trees } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./components/ui/tooltip.jsx";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const palette = { bg: "var(--hh-bg)", surface: "var(--hh-surface)", primary: "var(--hh-primary)", accent: "var(--hh-accent)", text: "var(--hh-text)", muted: "var(--hh-muted)" };

function useStates(){ const [states,setStates]=useState([]); const [loading,setLoading]=useState(false); const {toast}=useToast(); useEffect(()=>{let m=true;(async()=>{try{setLoading(true);const res=await axios.get(`${API}/states`); if(!m)return; setStates(res.data||[]);}catch(e){toast({title:"Failed to load states",description:e?.message||""});}finally{setLoading(false);}})();return()=>m=false;},[toast]); return {states,loading}; }

function CompactWeatherWidget() {
  const [place, setPlace] = useState("");
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const getWindDirection = (degrees) => {
    if (!degrees && degrees !== 0) return '-';
    const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
    const index = Math.round(degrees / 22.5) % 16;
    return directions[index];
  };

  const getWindIntensity = (windSpeed) => {
    if (!windSpeed && windSpeed !== 0) return '';
    if (windSpeed <= 10) return '(low)';
    if (windSpeed <= 20) return '(mid)';
    return '(high)';
  };

  const onFetch = async () => {
    if (!place) {
      toast({ title: "Enter a location" });
      return;
    }
    try {
      setLoading(true);
      const res = await axios.get(`${API}/weather`, { params: { place, days } });
      setData(res.data);
    } catch (e) {
      toast({ title: "Weather fetch failed", description: e?.response?.data?.detail || e?.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="bg-black/40 border-white/20 backdrop-blur-sm p-4 text-white w-full max-w-md" data-testid="compact-weather-widget">
      <div className="mb-3">
        <h3 className="text-sm font-semibold text-white mb-2">Weather Forecast</h3>
        <div className="flex gap-2 mb-3">
          <Input
            placeholder="City, state"
            value={place}
            onChange={e => setPlace(e.target.value)}
            className="flex-1 h-8 text-xs bg-black/30 border-white/30 text-white placeholder:text-white/50"
            data-testid="compact-weather-input"
          />
          <Select value={String(days)} onValueChange={v => setDays(Number(v))}>
            <SelectTrigger className="w-16 h-8 text-xs bg-black/30 border-white/30 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[3, 5, 7, 10, 14, 16].map(d => (
                <SelectItem key={d} value={String(d)}>{d}d</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={onFetch}
            size="sm"
            className="h-8 px-3 text-xs"
            style={{ backgroundColor: palette.primary, color: "#04130E" }}
            data-testid="compact-weather-button"
          >
            Get
          </Button>
        </div>
      </div>

      {loading && (
        <div className="text-xs text-white/70 text-center py-4">Loading...</div>
      )}

      {data && (
        <div data-testid="compact-weather-results">
          <div className="mb-2 text-center">
            <p className="text-xs font-medium text-white">{data.place}</p>
            <p className="text-xs text-white/60">{days}-day forecast</p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-white/20">
                  <th className="text-left py-1 pr-2 text-white/80 font-medium">Date</th>
                  <th className="text-center py-1 px-1 text-white/80 font-medium">High</th>
                  <th className="text-center py-1 px-1 text-white/80 font-medium">Low</th>
                  <th className="text-center py-1 px-1 text-white/80 font-medium">Rain</th>
                  <th className="text-center py-1 px-1 text-white/80 font-medium">Wind</th>
                  <th className="text-center py-1 pl-1 text-white/80 font-medium">Dir</th>
                </tr>
              </thead>
              <tbody>
                {data.days.slice(0, days).map((d, idx) => (
                  <tr key={d.date} className="border-b border-white/10 hover:bg-white/5">
                    <td className="py-1 pr-2 text-white/90">
                      {idx === 0 ? 'Today' : 
                       idx === 1 ? 'Tomorrow' : 
                       new Date(d.date).toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' })}
                    </td>
                    <td className="text-center py-1 px-1 text-orange-300 font-medium">
                      {d.temp_max ? `${Math.round(d.temp_max)}°` : '-'}
                    </td>
                    <td className="text-center py-1 px-1 text-blue-300 font-medium">
                      {d.temp_min ? `${Math.round(d.temp_min)}°` : '-'}
                    </td>
                    <td className="text-center py-1 px-1 text-cyan-300">
                      {d.precipitation_prob ? `${d.precipitation_prob}%` : '-'}
                    </td>
                    <td className="text-center py-1 px-1 text-gray-300">
                      {d.windspeed_max ? (
                        <span>
                          {Math.round(d.windspeed_max)}
                          <span className="text-xs text-gray-400 ml-1">{getWindIntensity(d.windspeed_max)}</span>
                        </span>
                      ) : '-'}
                    </td>
                    <td className="text-center py-1 pl-1 text-green-300 text-xs">
                      {getWindDirection(d.winddirection_max)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Card>
  );
}

function Hero({ onExplore }){
  const { states } = useStates();
  const stateCount = states.length || 4; // Default to 4 until states load
  
  return (<section className="hero-wrap-compact" style={{background:'transparent'}}>
    <div className="container mx-auto px-6 pt-8 pb-4">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* Left Column - Main Hero Content */}
        <div className="lg:col-span-2">
          <h1 className="hero-title text-2xl lg:text-3xl font-bold tracking-tight font-serif mt-3" data-testid="hero-title">Hunters' Hub</h1>
          <p className="hero-subtitle mt-2 max-w-2xl text-base lg:text-lg" data-testid="hero-subtitle">Regulations, seasons, species anatomy, gear, and local resources.</p>
          {/* Explore Now button removed for cleaner hero design */}
        </div>
        
        {/* Right Column - Compact Weather Widget */}
        <div className="lg:justify-self-end w-full lg:w-auto">
          <CompactWeatherWidget />
        </div>
      </div>
    </div>
  </section>);
}

function ExploreByTopic(){ 
  const {states}=useStates(); 
  const [selected,setSelected]=useState(""); 
  const navigate=useNavigate(); 
  
  const handleStateChange = (value) => {
    if (value === "select") {
      setSelected("");
    } else {
      setSelected(value);
    }
  };
  
  const go=(tab)=>{ 
    if(!selected || selected === "select") return; 
    navigate(`/state/${selected}?tab=${tab}`);
  }; 
  
  const tips={regulations:"Legal methods, bag limits, land rules",seasons:"Species calendars and zone dates",resources:"Licensing, WMAs, agency contacts"}; 
  const topics=[{key:"regulations",title:"Regulations",desc:"Legal methods, bag limits, public vs private land.",cta:"View Regulations",Icon:Scale},{key:"seasons",title:"Seasons",desc:"By species, zones, and method – verify dates.",cta:"View Seasons",Icon:CalendarDays},{key:"resources",title:"Local Resources",desc:"Licensing, WMAs, access programs, agency contacts.",cta:"Open Resources",Icon:MapPinned},]; 
  
  const selectedStateName = states.find(s => s.code === selected)?.name;
  
  return (<section className="container mx-auto px-6 py-10" data-testid="explore-topic-section">
  <div className="flex items-center justify-between gap-4 flex-wrap">
    <div>
      <h2 className="section-title text-3xl font-bold font-serif" data-testid="explore-topic-title">Explore by Topic</h2>
      <p className="section-subtitle text-sm mt-1" data-testid="explore-topic-subtitle">Pick a state for state‑specific items. Species/gear live below.</p>
    </div>
    <div className="min-w-[260px]" data-testid="explore-state-select">
      <Select value={selected} onValueChange={handleStateChange}>
        <SelectTrigger data-testid="explore-state-trigger" className="rounded-full h-11 bg-black/40 border-white/40 text-white shadow-lg backdrop-blur-sm hover:bg-black/50 hover:border-white/50 focus:ring-2 focus:ring-white/30">
          <SelectValue placeholder="Select a state"/>
        </SelectTrigger>
        <SelectContent className="max-h-72">
          <SelectItem value="select" data-testid="explore-state-item-default">Select a state</SelectItem>
          {states.map(s=>(<SelectItem key={s.code} value={s.code} data-testid={`explore-state-item-${s.code.toLowerCase()}`}>{s.name} ({s.code})</SelectItem>))}
        </SelectContent>
      </Select>
    </div>
  </div>
  
  {selected && selected !== "select" && (
    <>
      <div className="mt-4 text-center">
        <p className="text-white/80 text-sm">Showing topics for <span className="text-white font-semibold">{selectedStateName}</span></p>
      </div>
      <TooltipProvider delayDuration={150}>
        <div className="grid md:grid-cols-3 gap-5 mt-6 animate-in slide-in-from-top-4 duration-300">
          {topics.map(t=> (
            <Card key={t.key} className="topic-card p-5 text-white" data-testid={`topic-card-${t.key}`}>
              <div className="topic-card-inner">
                <Tooltip><TooltipTrigger asChild><div className="flex items-center gap-3 cursor-default" data-testid={`topic-card-head-${t.key}`}>
                  <div className="topic-icon" data-testid={`topic-card-icon-${t.key}`}><t.Icon size={20}/></div><h3 className="text-lg font-semibold">{t.title}</h3></div></TooltipTrigger><TooltipContent><p>{tips[t.key]}</p></TooltipContent></Tooltip>
                <p className="text-white/75 text-sm mt-2">{t.desc}</p>
                <div className="mt-4 flex items-center gap-4">
                  <Button className="rounded-full" onClick={()=>go(t.key)} data-testid={`topic-card-cta-${t.key}`} style={{backgroundColor:palette.primary,color:"#04130E"}}>{t.cta}</Button>
                  <Link to={`/topic/${t.key}`} className="text-white/80 underline underline-offset-4 text-sm" data-testid={`topic-card-learn-${t.key}`}>Learn more</Link>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </TooltipProvider>
    </>
  )}
  
  {(!selected || selected === "select") && (
    <div className="mt-8 text-center">
      <div className="inline-flex items-center gap-3 px-6 py-4 rounded-xl bg-white/5 border border-white/10 text-white/70">
        <Scale size={20} className="text-white/50"/>
        <p className="text-sm">Please select a state to view hunting topics and regulations</p>
      </div>
    </div>
  )}
</section>);} 

function KnowledgeTiles(){ return (<section className="container mx-auto px-6 py-10" data-testid="knowledge-tiles">
  <div className="flex items-end justify-between gap-4 flex-wrap">
    <div>
      <h2 className="section-title text-3xl font-bold font-serif" data-testid="knowledge-title">Knowledge Center</h2>
      <p className="section-subtitle text-sm mt-1" data-testid="knowledge-subtitle">Global guides that don’t change by state.</p>
    </div>
  </div>
  <div className="grid md:grid-cols-2 gap-5 mt-6">
    <Card className="knowledge-tile p-6 text-white" data-testid="knowledge-tile-species">
      <div className="flex items-center gap-3"><div className="topic-icon"><PawPrint size={20}/></div><h3 className="text-xl font-semibold">Species Anatomy &amp; Habitat</h3></div>
      <p className="text-white/75 text-sm mt-2">Vitals, behavior, best timing, and periods to avoid for common U.S./Canada species.</p>
      <div className="mt-4"><Link to="/species"><Button className="rounded-full" style={{backgroundColor:palette.primary,color:"#04130E"}} data-testid="knowledge-species-cta">Open Species Library</Button></Link></div>
    </Card>
    <Card className="knowledge-tile p-6 text-white" data-testid="knowledge-tile-gear">
      <div className="flex items-center gap-3"><div className="topic-icon"><Backpack size={20}/></div><h3 className="text-xl font-semibold">Gear &amp; Prep</h3></div>
      <p className="text-white/75 text-sm mt-2">Loadouts, safety, and field craft for waterfowl, big game, treestands and blinds.</p>
      <div className="mt-4"><Link to="/gear"><Button className="rounded-full" style={{backgroundColor:palette.primary,color:"#04130E"}} data-testid="knowledge-gear-cta">Open Gear &amp; Prep</Button></Link></div>
    </Card>
  </div>
</section>);} 

// Old weather widget removed - replaced with compact version in hero

function HomePage(){ const onExplore=()=>{ const el=document.querySelector('[data-testid="explore-topic-section"]'); if(el) el.scrollIntoView({behavior:"smooth"});}; return (<><Hero onExplore={onExplore}/><ExploreByTopic/><KnowledgeTiles/></>); }

function Shell(){ useEffect(()=>{ const handler=()=>{ const y=Math.min(window.scrollY*0.06,80); document.documentElement.style.setProperty('--parallax-y',`${y}px`);}; handler(); window.addEventListener('scroll',handler,{passive:true}); return ()=>window.removeEventListener('scroll',handler);},[]); return (<div className="min-h-screen app-bg global-parallax" data-testid="app-shell"><NavBar/><Outlet/><Footer/><Toaster/></div>); }

function NavBar(){ const location = useLocation(); const hideGlobal=location.pathname.startsWith('/state/'); const scrollToWeather = () => { const el = document.querySelector('.hero-wrap-compact'); if(el) el.scrollIntoView({behavior:"smooth"}); }; return (<nav className="sticky top-0 z-30 backdrop-blur-md border-b border-white/10" style={{background:"rgba(6,12,10,0.6)"}} data-testid="navbar"><div className="container mx-auto px-6 h-14 flex items-center justify-between"><Link to="/" className="font-bold tracking-wide text-white font-serif" data-testid="brand">Hunters' Hub</Link><div className="flex items-center gap-5">{!hideGlobal && (<><Link to="/species" className="text-sm text-white/80" data-testid="nav-species">Species</Link><Link to="/gear" className="text-sm text-white/80" data-testid="nav-gear">Gear</Link></>)}<button onClick={scrollToWeather} className="text-sm text-white/80 hover:text-white transition-colors" data-testid="nav-weather">Weather</button></div></div></nav>);} 

function Footer(){
  const year = new Date().getFullYear();
  return (
    <footer className="border-t border-white/10 mt-8 bg-black/40" data-testid="footer">
      <div className="container mx-auto px-6 py-8">
        <div className="rounded-lg bg-black/50 border border-white/10 p-6 text-white/80">
          <h3 className="text-white font-semibold mb-2">Educational Resources</h3>
          <p className="text-sm mb-4">Hunter education information based on established safety standards and best practices from recognized organizations committed to hunting safety and conservation.</p>
          <div className="flex items-center gap-3 text-sm">
            <span className="text-emerald-300">Hunter‑Ed.com</span>
            <span className="text-yellow-500">•</span>
            <span className="text-emerald-300">Kalkomey Enterprises</span>
            <span className="text-yellow-500">•</span>
            <span className="text-emerald-300">State Wildlife Agencies</span>
          </div>
        </div>

        <div className="flex items-center justify-center text-sm text-white/60 mt-8">
          <div>© {year} HuntBase. All rights reserved.</div>
        </div>
      </div>
    </footer>
  );
}

function App(){ useEffect(()=>{(async()=>{try{await axios.get(`${API}/`);}catch(e){}})();},[]); return (<BrowserRouter><Routes><Route element={<Shell/>}><Route path="/" element={<HomePage/>}/><Route path="/state/:code" element={<StateHub/>}/><Route path="/topic/:key" element={<TopicInfo/>}/><Route path="/species" element={<SpeciesLibrary/>}/><Route path="/gear" element={<GearPrep/>}/><Route path="/wildlife-areas" element={<WildlifeAreas/>}/></Route></Routes></BrowserRouter>); }

export default App;